import { FormikErrors, FormikHelpers, useFormik } from 'formik'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { InfoIcon } from '@icons/InfoIcon'
import { NavigationArrowDownIcon } from '@icons/NavigationArrowDownIcon'

import { InfoCard } from '@ui/Popups/InfoCard/InfoCard'
import { Body1 } from '@ui/Typography'
import { CheckBox } from '@ui/v2/CheckBox/CheckBox'
import { DropdownInput } from '@ui/v2/DropdownInput/DropdownInput'
import { Input } from '@ui/v2/Input/Input'
import { ModalContentWrapper } from '@ui/v2/Modal/ModalContentWrapper'

import { useWindowResize } from '@utils/domUtils'
import { isAllowedNumericKey } from '@utils/input.utils'
import { isNumber, maxNumber, minNumber } from '@utils/validations'

import { IntroducingBrokerRebateGroupDto } from '@model/IntroducingBrokerRebateGroupDto'
import { NameDto } from '@model/NameDto'

import { FilterQueryProps } from '@global/filter/FilterQueryModal'

import styles from './GroupModal.module.scss'

interface GroupModalProps {
  closeModal: () => void
  groupData?: Partial<IntroducingBrokerRebateGroupDto>
  onSubmit: (values: Partial<GroupFormValues>) => Promise<void>
  isCreateGroup: boolean
  approvalTypes: NameDto[]
  rebatePeriods: NameDto[]
  onOpenRebateApprovalTypeModal: () => void
  onOpenRebatePeriodModal: () => void
  onSetSelectedGroup: (selectedGroup: Partial<IntroducingBrokerRebateGroupDto>) => void
}

export const GroupModal: FC<GroupModalProps> = (props) => {
  const {
    closeModal,
    groupData,
    onSubmit,
    approvalTypes,
    rebatePeriods,
    onOpenRebateApprovalTypeModal,
    onOpenRebatePeriodModal,
    isCreateGroup,
  } = props
  const [isCreateMode] = useState(isCreateGroup) // Store the initial value

  const [initialValues] = useState(
    groupData
      ? {
          name: groupData?.name ?? '',
          rebateAmountInPercentage: groupData?.rebateAmountInPercentage
            ? groupData?.rebateAmountInPercentage.toString()
            : '',
          rebateApprovalTypeId: groupData?.rebateApprovalType?.id,
          rebatePeriodId: groupData?.rebatePeriod?.id,
          isDefault: groupData?.isDefault ?? false,
        }
      : undefined
  )

  const { t } = useTranslation()

  const isMobile = useWindowResize()

  const { setFieldValue, errors, values, handleSubmit, setErrors, resetForm, isSubmitting } =
    useGroupForm({
      initialValues: initialValues,
      handleSubmit: async (values, { resetForm, setSubmitting }) => {
        setSubmitting(true)
        await onSubmit(values)
        setSubmitting(false)
        resetForm()
      },
    })

  const hasErrors = useMemo(() => Object.values(errors).some((error) => !!error), [errors])

  const isApplyDisabled =
    hasErrors ||
    isSubmitting ||
    !values.name ||
    !values.rebateApprovalTypeId ||
    !values.rebatePeriodId ||
    !values.rebateAmountInPercentage

  const handleChange = (key: string, value: any) => {
    setFieldValue(key, value)
    props.onSetSelectedGroup({ ...groupData, [key]: value })

    if (hasErrors) {
      setErrors({
        ...errors,
        [key]: undefined,
      })
    }
  }

  const handleClose = () => {
    setErrors({})
    resetForm()
    closeModal()
  }

  return (
    <ModalContentWrapper
      title={
        isCreateMode
          ? t('IB.RebatesPage.GroupsPage.New Group')
          : t('IB.RebatesPage.GroupsPage.Update Group settings')
      }
      applyLabel={isCreateMode ? t('IB.RebatesPage.GroupsPage.Create a Group') : t('Apply')}
      isApplyDisabled={isApplyDisabled}
      onClose={handleClose}
      onConfirm={handleSubmit}
      onCancel={handleClose}
    >
      <div className={styles.groupModal}>
        <Input
          value={values.name}
          placeholder={t('IB.RebatesPage.GroupsPage.Group name')}
          onChange={(e) => {
            handleChange('name', e.target.value)
          }}
          hint={errors.name}
          error={!!errors.name}
        />
        {isMobile ? (
          <Input
            value={approvalTypes.find((type) => type.id === values.rebateApprovalTypeId)?.name}
            placeholder={t('IB.RebatesPage.GroupsPage.Group type')}
            readOnly
            onClick={onOpenRebateApprovalTypeModal}
            hint={errors.rebateApprovalTypeId}
            error={!!errors.rebateApprovalTypeId}
            endIcon={NavigationArrowDownIcon}
            onEndIconClick={onOpenRebateApprovalTypeModal}
          />
        ) : (
          <DropdownInput
            value={values.rebateApprovalTypeId}
            onChange={(option) => handleChange('rebateApprovalTypeId', option.id)}
            options={approvalTypes}
            placeholder={t('IB.RebatesPage.GroupsPage.Group type')}
            hint={errors.rebateApprovalTypeId}
            error={!!errors.rebateApprovalTypeId}
          />
        )}
        {isMobile ? (
          <Input
            value={rebatePeriods.find((type) => type.id === values.rebatePeriodId)?.name}
            placeholder={t('IB.RebatesPage.GroupsPage.Payout period')}
            readOnly
            onClick={onOpenRebatePeriodModal}
            hint={errors.rebatePeriodId}
            error={!!errors.rebatePeriodId}
            endIcon={NavigationArrowDownIcon}
            onEndIconClick={onOpenRebatePeriodModal}
          />
        ) : (
          <DropdownInput
            value={values.rebatePeriodId}
            onChange={(option) => handleChange('rebatePeriodId', option.id)}
            options={rebatePeriods}
            placeholder={t('IB.RebatesPage.GroupsPage.Payout period')}
            hint={errors.rebatePeriodId}
            error={!!errors.rebatePeriodId}
          />
        )}
        <Input
          value={values.rebateAmountInPercentage}
          placeholder={t('IB.RebatesPage.Rebate amount in %')}
          onChange={(e) => handleChange('rebateAmountInPercentage', e.target.value)}
          hint={errors.rebateAmountInPercentage}
          error={!!errors.rebateAmountInPercentage}
          onKeyDown={(e) => !isAllowedNumericKey(e) && e.preventDefault()}
        />
        <div
          className={styles.checkbox}
          onClick={() => handleChange('isDefault', !values.isDefault)}
        >
          <CheckBox value={!!values.isDefault} />

          <Body1>{t('IB.RebatesPage.GroupsPage.Make group default')}</Body1>

          <span onClick={(e) => e.stopPropagation()}>
            <InfoCard
              text={t(
                'IB.RebatesPage.GroupsPage.If selected, all new TAs will be automatically assigned to that Group'
              )}
            >
              <InfoIcon color='textLowEm' size={20} />
            </InfoCard>
          </span>
        </div>
      </div>
    </ModalContentWrapper>
  )
}

export interface GroupFormValues {
  name: string
  rebateApprovalTypeId: number
  rebatePeriodId: number
  rebateAmountInPercentage: string
  isDefault: boolean
}

interface GroupFormParams {
  handleSubmit: (
    values: Partial<GroupFormValues>,
    formikHelpers: FormikHelpers<Partial<GroupFormValues>>
  ) => void
  initialValues?: Partial<GroupFormValues>
}

const DEFAULT_VALUES: Partial<GroupFormValues> = {
  name: '',
  rebateApprovalTypeId: undefined,
  rebatePeriodId: undefined,
  rebateAmountInPercentage: '',
  isDefault: false,
}

export const useGroupForm = ({
  handleSubmit,
  initialValues = DEFAULT_VALUES,
}: GroupFormParams): ReturnType<typeof useFormik<Partial<GroupFormValues>>> => {
  const { t } = useTranslation()

  return useFormik<Partial<GroupFormValues>>({
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validate: (values) => {
      const errors: FormikErrors<FilterQueryProps> = {}

      if (!values.name) {
        errors.name = t('Validation.Required')
      }

      if (!values.rebateApprovalTypeId) {
        errors.rebateApprovalTypeId = t('Validation.Required')
      }

      if (!values.rebatePeriodId) {
        errors.rebatePeriodId = t('Validation.Required')
      }

      if (!values.rebateAmountInPercentage) {
        errors.rebateAmountInPercentage = t('Validation.Required')
      }

      if (values.rebateAmountInPercentage && !isNumber(values.rebateAmountInPercentage)) {
        errors.rebateAmountInPercentage = t('Validation.Number')
      }

      if (
        values.rebateAmountInPercentage &&
        (minNumber(values.rebateAmountInPercentage, 0.1) ||
          maxNumber(values.rebateAmountInPercentage, 100))
      ) {
        errors.rebateAmountInPercentage = t(
          'IB.RebatesPage.ApprovalsPage.Please enter a valid percentage between 0 and 100'
        )
      }

      return errors
    },
    validateOnChange: false,
  })
}
